// assets/js/utils/nanp-region.js
/**
 * Which country is a +1 number in? US, Canada, or another NANP country.
 *
 * ── Why this exists ──────────────────────────────────────────────────────
 * A 'mobile' guest on a US number goes SMS-direct: Meta stopped delivering
 * WhatsApp marketing templates to US numbers in April 2025 (131049), so a
 * WhatsApp attempt there is guaranteed to fail. That rule was written as
 * "11 digits starting with 1", which is every +1 number, not the US. Canada,
 * Jamaica, the Bahamas, the Dominican Republic and the rest of the NANP share
 * +1, Meta's pause does not cover them, and WhatsApp is how those guests are
 * actually reached. A Canadian host testing on their own Canadian number got
 * an SMS instead (support chat, 2026-09-28), and every Canadian 'mobile'
 * guest was sent the same way.
 *
 * The country is in the area code. The lists below are every code assigned
 * to Canada and to the non-US NANP countries, including announced overlays
 * that are not in service yet: a code on these lists is never a US code, so
 * a planned one costs nothing. Anything not listed is treated as US,
 * including the US territories (USVI, Guam, the Marianas, American Samoa),
 * which is what the old rule did for them.
 *
 * Puerto Rico is the exception (2026-10-30; not texted at all since 2026-10-31, see UNTEXTABLE_NANP_AREA_CODES). Meta defines the pause as "+1
 * and a US area code", and its WhatsApp rate card files 787/939 as their
 * own market, Puerto Rico, under Rest of Latin America, not the US. Brevo
 * texts to Puerto Rico have also never delivered: every one we sent
 * soft-bounced ("reasons unknown"). As US, a Puerto Rico guest got nothing.
 * The other territories are not named on Meta's card, so they stay US.
 */

// Canada: every province and territory, overlays, and the non-geographic
// Canadian service codes (600, 622, 633…).
const CANADA_AREA_CODES = new Set([
  // AB
  '368', '403', '568', '587', '780', '825',
  // BC
  '236', '250', '257', '604', '672', '778',
  // MB
  '204', '431', '584',
  // NB
  '428', '506',
  // NL
  '709', '879',
  // NS / PEI
  '782', '851', '902',
  // ON
  '226', '249', '289', '343', '365', '382', '387', '416', '437', '460', '519',
  '537', '548', '613', '647', '683', '705', '742', '753', '807', '905', '942',
  // QC
  '263', '354', '367', '418', '438', '450', '468', '514', '579', '581', '819', '873',
  // SK
  '306', '474', '639',
  // YT / NT / NU
  '867',
  // Non-geographic Canadian services
  '600', '622', '633', '638', '644', '655', '677', '688'
]);

// Other NANP countries, and Puerto Rico (the other US territories stay US).
const OTHER_NANP_AREA_CODES = new Set([
  '242', // Bahamas
  '246', // Barbados
  '264', // Anguilla
  '268', // Antigua and Barbuda
  '284', // British Virgin Islands
  '345', // Cayman Islands
  '441', // Bermuda
  '473', // Grenada
  '649', // Turks and Caicos
  '658', '876', // Jamaica
  '664', // Montserrat
  '721', // Sint Maarten
  '758', // Saint Lucia
  '767', // Dominica
  '784', // Saint Vincent and the Grenadines
  '809', '829', '849', // Dominican Republic
  '868', // Trinidad and Tobago
  '869', // Saint Kitts and Nevis
  '787', '939' // Puerto Rico: a US territory, but its own WhatsApp market (see above)
]);

/**
 * @param {string} phone E.164-ish, any punctuation.
 * @returns {'US'|'CA'|'NANP'|null} null when this is not an 11-digit +1 number.
 */
function nanpRegion(phone) {
  const digits = String(phone == null ? '' : phone).replace(/\D/g, '');
  if (digits.length !== 11 || digits[0] !== '1') return null;
  const area = digits.slice(1, 4);
  if (CANADA_AREA_CODES.has(area)) return 'CA';
  if (OTHER_NANP_AREA_CODES.has(area)) return 'NANP';
  return 'US';
}

/** A US (or US-territory, bar Puerto Rico) number: the only kind a 'mobile' guest gets SMS-direct on. */
function isUSPhone(phone) {
  return nanpRegion(phone) === 'US';
}

/**
 * Countries outside +1 whose guests may be TEXTED after their WhatsApp
 * fails. Never SMS-direct: they still get WhatsApp first.
 *
 * '55' Brazil (2026-10-17): Meta blocked our WhatsApp account from Brazilian
 * numbers from 2026-10-14 (130497), so a phone-only Brazilian guest got
 * nothing. Not tested on a real Brazilian SIM before it went live (two public
 * test numbers bounced as invalid); the first real fallbacks are the test,
 * and a bounced text falls back to email by itself.
 */
const SMS_FALLBACK_COUNTRY_CODES = ['55'];

/**
 * +1 area codes we never text, although they are +1.
 *
 * Puerto Rico (2026-10-31): Brevo has never delivered a text to 787/939.
 * All 8 numbers we texted in 90 days soft-bounced "reasons unknown",
 * including the host's own phone six times (Santiago/Anzueta wedding,
 * 2026-10-29/30). The backup text after a failed WhatsApp was a dead hop. It
 * cost a credit, it still read "sent" (4 of those 6 bounces never reached our
 * webhook), and the email backup waits on that bounce. A number we do not text
 * gets the email at once. An SMS_COUNTRY_ALLOWLIST entry of '1787' / '1939'
 * turns one back on.
 */
const UNTEXTABLE_NANP_AREA_CODES = new Set(['787', '939']);

/**
 * May this number be texted (Brevo), after a WhatsApp failure or on an SMS
 * send? Every 11-digit +1 number (US, Canada, the rest of the NANP) except
 * UNTEXTABLE_NANP_AREA_CODES, plus SMS_FALLBACK_COUNTRY_CODES. The ONE rule
 * for the server's send gate (messaging.service sendSMS), its fallback
 * decision (isSMSFallbackAllowed) and the wizard's SMS-consent prediction.
 * `extraPrefixes`: the server's SMS_COUNTRY_ALLOWLIST env opt-in, which the
 * browser cannot see.
 * @returns {{allowed: boolean, countryPrefix: string}}
 */
function mayBeTexted(phone, extraPrefixes = []) {
  const digits = String(phone == null ? '' : phone).replace(/\D/g, '');
  const extra = (extraPrefixes || []).map(p => String(p).replace(/\D/g, '')).filter(Boolean);
  if (digits.length === 11 && digits[0] === '1') {
    const areaPrefix = digits.slice(0, 4);
    if (UNTEXTABLE_NANP_AREA_CODES.has(areaPrefix.slice(1)) && !extra.includes(areaPrefix)) {
      return { allowed: false, countryPrefix: areaPrefix };
    }
    return { allowed: true, countryPrefix: '1' };
  }
  const codes = SMS_FALLBACK_COUNTRY_CODES.concat(extra);
  const cc = codes.find(c => digits.startsWith(c));
  if (cc && digits.length >= 10 && digits.length <= 15) return { allowed: true, countryPrefix: cc };
  return { allowed: false, countryPrefix: digits.substring(0, Math.min(3, digits.length)) };
}

const NanpRegion = { nanpRegion, isUSPhone, mayBeTexted, SMS_FALLBACK_COUNTRY_CODES, UNTEXTABLE_NANP_AREA_CODES, CANADA_AREA_CODES, OTHER_NANP_AREA_CODES };

// Dual-environment, matching assets/js/utils/phone-shape.js: the browser gets
// the global, the server requires this same file, so the send wizard's
// prediction and the server's routing cannot disagree about a number.
if (typeof window !== 'undefined') window.NanpRegion = NanpRegion;
if (typeof module !== 'undefined' && module.exports) module.exports = NanpRegion;
